'use client';

import {Box, Button, Container, Stack, Typography} from '@mui/material';
import {useTranslations} from 'next-intl';
import {Link} from '@/i18n/navigation';
import {routeFactory} from '@/routing/route-factory';

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const t = useTranslations('Error');
  const isDev = process.env.NODE_ENV === 'development';

  return (
    <Container maxWidth='sm'>
      <Stack
        spacing={3}
        sx={{
          alignItems: 'center',
          textAlign: 'center',
          minHeight: '70vh',
          justifyContent: 'center',
          py: 10,
        }}
      >
        <Typography
          aria-hidden
          sx={(theme) => ({
            fontWeight: 700,
            fontSize: 'clamp(4rem, 18vw, 8rem)',
            lineHeight: 1,
            color: theme.palette.primary.main,
            letterSpacing: '-0.04em',
          })}
        >
          !
        </Typography>

        <Typography variant='h2' color='textDark'>
          {t('heading')}
        </Typography>

        <Typography variant='body1' color='textDark' sx={{maxWidth: 480}}>
          {t('description')}
        </Typography>

        <Stack
          direction={{xs: 'column', sm: 'row'}}
          sx={{pt: 1, width: {xs: '100%', sm: 'auto'}, gap: 2}}
        >
          <Button
            component={Link}
            href={routeFactory.home.root()}
            variant='outlined'
            color='primary'
            disableElevation
            size='large'
          >
            {t('backToHome')}
          </Button>
          <Button
            variant='contained'
            color='primary'
            onClick={reset}
            disableElevation
            size='large'
          >
            {t('tryAgain')}
          </Button>
        </Stack>

        {error.digest && (
          <Typography variant='caption' color='text.secondary' sx={{pt: 2}}>
            {t('reference')}: {error.digest}
          </Typography>
        )}

        {isDev && error.message && (
          <Box
            component='pre'
            sx={(theme) => ({
              mt: 2,
              p: 2,
              width: '100%',
              maxWidth: 560,
              textAlign: 'left',
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
              borderRadius: theme.shape.borderRadiusMd / 8,
              fontSize: '0.8rem',
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            })}
          >
            {error.message}
          </Box>
        )}
      </Stack>
    </Container>
  );
}